import React, { useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface Particle {
  x: number;
  y: number;
  size: number;
  baseX: number;
  baseY: number;
  density: number;
  color: string;
  // New properties for neuronal connection effect
  isConnector: boolean;
  connectionRadius: number;
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouse = useRef<{ x: number | null; y: number | null; radius: number }>({
    x: null,
    y: null,
    radius: 150,
  });
  const animationFrameId = useRef<number>(0);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particlesArray: Particle[] = [];

    // Theme-aware colors
    const isDark = resolvedTheme === 'dark';
    const bgColor = isDark ? '#0d1830' : '#ffffff';
    const particleColors = isDark
      ? ['#ffffff10', '#ffffff20', '#a3f1e820', '#a3f1e830', '#66cdaa40']
      : ['#17c48015', '#1dbcbc20', '#14a87a20', '#1dbcbc30', '#17c48040'];
    const connectionColor = isDark
      ? (opacity: number) => `rgba(220, 255, 255, ${opacity})`
      : (opacity: number) => `rgba(15, 120, 80, ${opacity * 1.8})`;

    const getBackgroundGradient = () => {
        if (!canvas) return '';
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, bgColor);
        gradient.addColorStop(0.5, bgColor);
        gradient.addColorStop(1, bgColor);
        return gradient;
    };

    const init = () => {
      particlesArray = [];
      if (!canvas || canvas.width === 0 || canvas.height === 0) return;
      const numberOfParticles = (canvas.height * canvas.width) / 2000;
      for (let i = 0; i < numberOfParticles; i++) {
        const size = (Math.random() * 4.5) + 1;
        const x = (Math.random() * canvas.width);
        const y = (Math.random() * canvas.height);
        const isConnector = Math.random() > 0.85; // 15% of particles are connectors

        particlesArray.push({
          x,
          y,
          size,
          baseX: x,
          baseY: y,
          density: (Math.random() * 20) + 5,
          color: particleColors[Math.floor(Math.random() * particleColors.length)],
          isConnector,
          // Assign a connection radius to connectors
          connectionRadius: isConnector ? (Math.random() * 80) + 70 : 0, // Radius 70-150px
        });
      }
    };

    const setCanvasSize = () => {
        if (!canvas) return;
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;

        // Only skip if we already have particles and size hasn't changed
        if (canvas.width === newWidth && canvas.height === newHeight && particlesArray.length > 0) {
            return;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;
        mouse.current.radius = (canvas.height / 80) * 20;
        init();
    };

    const animate = () => {
      animationFrameId.current = requestAnimationFrame(animate);
      // CRITICAL FIX: Do not run animation if canvas is not ready.
      if(!ctx || canvas.width === 0 || canvas.height === 0) return;


      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = getBackgroundGradient();
      ctx.fillRect(0,0, canvas.width, canvas.height);

      let particlesUnderMouse: Particle[] = [];

      // 1. Update particle positions and collect those under the mouse
      for (let i = 0; i < particlesArray.length; i++) {
        const p = particlesArray[i];

        let total_dx = 0;
        let total_dy = 0;

        if (mouse.current.x !== null && mouse.current.y !== null) {
            let dx_mouse = p.x - mouse.current.x;
            let dy_mouse = p.y - mouse.current.y;
            let distance = Math.sqrt(dx_mouse * dx_mouse + dy_mouse * dy_mouse);

            if (distance < mouse.current.radius && mouse.current.radius > 0) {
                particlesUnderMouse.push(p); // Collect for neuron logic
                const force = (mouse.current.radius - distance) / mouse.current.radius;
                const angle = Math.atan2(dy_mouse, dx_mouse);

                // Increased repulsion for more movement
                const repulsion = force * p.density * 0.2;
                total_dx += Math.cos(angle) * repulsion;
                total_dy += Math.sin(angle) * repulsion;

                // Increased swirl for more pronounced effect
                const swirlAngle = angle + Math.PI / 2;
                const swirl = force * 1.5;
                total_dx += Math.cos(swirlAngle) * swirl;
                total_dy += Math.sin(swirlAngle) * swirl;
            }
        }

        const return_dx = p.baseX - p.x;
        const return_dy = p.baseY - p.y;
        total_dx += return_dx * 0.01;
        total_dy += return_dy * 0.01;

        p.x += total_dx;
        p.y += total_dy;
      }

      // 2. Draw all particles (lighter = additive glow in dark; source-over = visible on white in light)
      ctx.globalCompositeOperation = isDark ? 'lighter' : 'source-over';
      for (const p of particlesArray) {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';

      // 3. Draw neuronal connections (optimized)
      const connectors = particlesUnderMouse.filter(p => p.isConnector);
      if (connectors.length > 0 && mouse.current.x !== null && mouse.current.y !== null) {
          ctx.lineWidth = isDark ? 0.25 : 0.6;
          for(const p1 of connectors) {
              const dx_mouse = p1.x - mouse.current.x;
              const dy_mouse = p1.y - mouse.current.y;
              const distanceToMouse = Math.sqrt(dx_mouse*dx_mouse + dy_mouse*dy_mouse);

              const opacity = mouse.current.radius > 0 ? Math.max(0, (1 - (distanceToMouse / mouse.current.radius)) * 0.7) : 0;
              if(opacity <= 0) continue;

              for(const p2 of particlesUnderMouse) {
                  if (p1 === p2) continue;

                  const dx_conn = p1.x - p2.x;
                  const dy_conn = p1.y - p2.y;
                  const distance_conn = Math.sqrt(dx_conn * dx_conn + dy_conn * dy_conn);

                  if (distance_conn < p1.connectionRadius) {
                      ctx.strokeStyle = connectionColor(opacity);
                      ctx.beginPath();
                      ctx.moveTo(p1.x, p1.y);
                      ctx.lineTo(p2.x, p2.y);
                      ctx.stroke();
                  }
              }
          }
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      mouse.current.x = event.clientX;
      mouse.current.y = event.clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        const touch = event.touches[0];
        mouse.current.x = touch.clientX;
        mouse.current.y = touch.clientY;
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        const touch = event.touches[0];
        mouse.current.x = touch.clientX;
        mouse.current.y = touch.clientY;
      }
    };

    const handleMouseOut = () => {
        mouse.current.x = null;
        mouse.current.y = null;
    };

    const handleTouchEnd = () => {
        mouse.current.x = null;
        mouse.current.y = null;
    };

    // Mouse events for desktop
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseOut);

    // Touch events for mobile
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    window.addEventListener('resize', setCanvasSize);

    // No need for scroll-based canvas resizing anymore since we use fixed calculation

    // Ensure proper initialization after DOM is ready
    const initializeCanvas = () => {
      setCanvasSize();
      animate();
    };

    // Try immediate initialization
    initializeCanvas();

    // Also try after a small delay to handle any timing issues
    const timeoutId = setTimeout(() => {
      if (particlesArray.length === 0) {
        initializeCanvas();
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId.current);
      // Remove mouse events
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseOut);
      // Remove touch events
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, [resolvedTheme]);

  return <canvas ref={canvasRef} className="fixed top-0 left-0 z-0" />;
};

export default AnimatedBackground;
